import { Injectable } from '@angular/core';

/**
 * SD-2450: lets a feature suppress the next global "chatEvent insert" error
 * dialog when it has already surfaced that failure inline.
 *
 * When an Allo turn fails (e.g. a mid-stream connection reset), the backend
 * emits a `chatStream` `error` event — which the chat renders as an interrupted
 * bubble with a Retry button — and *then* rethrows, which the WS layer reports as
 * a generic `ERR_GNR_INT` ("Internal error") on the failed `chatEvent`/`ins`
 * operation. Without this, the user sees both the inline bubble and a redundant
 * error dialog.
 *
 * The inline `chatStream` error always arrives before the generic operation
 * error (same backend call, sent first), so the chat marks the suppression
 * before the handler runs. A short TTL guards against a lingering flag if no
 * generic error follows.
 */
@Injectable({ providedIn: 'root' })
export class WsErrorSuppressionService {
  private static readonly WINDOW_MS = 3000;
  private suppressChatEventErrorUntil = 0;

  /**
   * Mark that the next failed `chatEvent`/`ins` operation has already been
   * surfaced inline (interrupted bubble), so its error dialog should be skipped.
   */
  suppressNextChatEventError(): void {
    this.suppressChatEventErrorUntil =
      Date.now() + WsErrorSuppressionService.WINDOW_MS;
  }

  /**
   * One-shot check: returns true (and clears the flag) when a `chatEvent`/`ins`
   * error dialog should be skipped because it was already surfaced inline.
   */
  consumeChatEventErrorSuppression(): boolean {
    const active = Date.now() <= this.suppressChatEventErrorUntil;
    this.suppressChatEventErrorUntil = 0;
    return active;
  }
}
